import { ReactNode, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useCinematicScroll } from "./useCinematicScroll";

gsap.registerPlugin(ScrollTrigger);

type Reveal = "zoom" | "pan-left" | "pan-right" | "dolly" | "fade-up" | "rise";

interface SceneProps {
  children: ReactNode;
  reveal?: Reveal;
  className?: string;
  /** Delay in seconds before the in-animation begins */
  delay?: number;
}

/**
 * Wraps a section and animates it with GSAP ScrollTrigger as it enters/leaves
 * the viewport. Designed to feel like one continuous camera move when adjacent
 * sections use complementary reveals (e.g. pan-left → pan-right).
 *
 * Falls back to a simple fade-in when prefers-reduced-motion or below 640px.
 */
export function Scene({ children, reveal = "fade-up", className, delay = 0 }: SceneProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { reduced } = useCinematicScroll();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reduced fallback: simple fade
    if (reduced) {
      gsap.fromTo(
        el,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        },
      );
      return;
    }

    let from: gsap.TweenVars = { opacity: 0, y: 60 };
    let to: gsap.TweenVars = { opacity: 1, y: 0 };

    switch (reveal) {
      case "zoom":
        from = { opacity: 0, scale: 0.92, filter: "blur(8px)" };
        to = { opacity: 1, scale: 1, filter: "blur(0px)" };
        break;
      case "pan-left":
        from = { opacity: 0, x: 80, rotateY: 6 };
        to = { opacity: 1, x: 0, rotateY: 0 };
        break;
      case "pan-right":
        from = { opacity: 0, x: -80, rotateY: -6 };
        to = { opacity: 1, x: 0, rotateY: 0 };
        break;
      case "dolly":
        from = { opacity: 0, scale: 0.94, y: 80, filter: "blur(6px)" };
        to = { opacity: 1, scale: 1, y: 0, filter: "blur(0px)" };
        break;
      case "rise":
        from = { opacity: 0, y: 100, scale: 0.97 };
        to = { opacity: 1, y: 0, scale: 1 };
        break;
      case "fade-up":
      default:
        from = { opacity: 0, y: 60 };
        to = { opacity: 1, y: 0 };
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(el, from, {
        ...to,
        duration: 1.1,
        delay,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 88%",
          end: "top 35%",
          scrub: 0.6,
        },
      });
    }, el);

    return () => ctx.revert();
  }, [reveal, delay, reduced]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        willChange: "transform, opacity, filter",
        transformStyle: "preserve-3d",
        perspective: 1200,
      }}
    >
      {children}
    </div>
  );
}
